import { useEffect, useMemo, useState } from "react";
import { Newspaper, ExternalLink, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface Article {
  title: string;
  link: string;
  pubDate: string;
  source: string;
}

interface LegislationNewsProps {
  limit?: number;
  heading?: string;
  eyebrow?: string;
  description?: string;
  showPipelineLink?: boolean;
}

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const LegislationNews = ({
  limit = 6,
  heading = "Legislation in the news",
  eyebrow = "Press Coverage",
  description = "Headlines tracking Alabama bills, statehouse action, and rulemaking — refreshed from public news feeds.",
  showPipelineLink = true,
}: LegislationNewsProps) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("fetch-legislation-news");
        if (error) throw error;
        if (!cancelled && data?.success && Array.isArray(data.articles)) {
          setArticles(data.articles);
        }
      } catch (err) {
        console.warn("Legislation news fetch failed", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { data: bills } = useQuery({
    queryKey: ["legislation-bill-ids"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("legislation")
        .select("bill_id");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const billIds = useMemo(
    () => (bills ?? []).map((b) => b.bill_id).filter(Boolean) as string[],
    [bills],
  );

  const matchedBill = (title: string) => {
    const upper = title.toUpperCase();
    return billIds.find((id) => {
      const compact = id.replace(/\s+/g, "");
      return (
        upper.includes(id.toUpperCase()) ||
        upper.replace(/\s+/g, "").includes(compact.toUpperCase())
      );
    });
  };

  const items = articles.slice(0, limit);

  return (
    <section className="py-20 bg-background border-t border-border">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">
              {eyebrow}
            </span>
            <h2 className="font-display text-3xl md:text-4xl font-bold mt-2 mb-3 flex items-center gap-3">
              <Newspaper className="h-6 w-6 text-primary" />
              {heading}
            </h2>
            <p className="text-muted-foreground max-w-2xl">{description}</p>
          </div>
          {showPipelineLink && (
            <a
              href="/legislation"
              className="text-sm font-semibold text-primary hover:underline shrink-0"
            >
              See the full pipeline →
            </a>
          )}
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: limit }).map((_, i) => (
              <div
                key={i}
                className="h-40 rounded-xl border border-border bg-card animate-pulse"
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-muted-foreground text-sm">
            No recent legislation headlines available.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((a) => {
              const bill = matchedBill(a.title);
              return (
                <a
                  key={a.link}
                  href={a.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-card border border-border rounded-xl p-5 flex flex-col gap-3 hover:border-primary/40 transition-colors"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                      {a.source}
                    </span>
                    {bill && (
                      <span className="text-xs font-mono font-semibold text-accent bg-accent/10 border border-accent/30 px-2 py-0.5 rounded-full">
                        {bill}
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-base font-semibold leading-snug group-hover:text-primary transition-colors">
                    {a.title}
                  </h3>
                  <div className="mt-auto flex items-center justify-between text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      {formatDate(a.pubDate)}
                    </span>
                    <ExternalLink className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100" />
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default LegislationNews;